// =============================================================================
// STREETSTICKERS — EGGSHELL STICKER PRICING ENGINE
// =============================================================================
// HYBRID model — kalibriert gegen Eggshellsticker.de Konkurrenzpreise:
//   linearPerUnit     = (SETUP_PER_STICKER + BASE_PER_CM2 * area_cm2) * shapeMult
//   stueckpreis_basis = max(MIN_UNIT_PRICE, linearPerUnit)
//   stueckpreis_final = stueckpreis_basis * (1 - mengenrabatt)
//   gesamtpreis       = max(MIN_ORDER_TOTAL, stueckpreis_final * menge)
//
// SETUP_PER_STICKER bildet branchenüblichen Sockel ab (Druckkopf-Setup, Stanzen).
// Macht kleine Sticker pro cm² teurer als grosse — wie bei der Konkurrenz.
//
// Aufschlag vs. Eggshellsticker.de: +2% bis +17% (Premium-Positionierung).
// =============================================================================

const PRICING = {
  BASE_PER_CM2:      0.004,  // EUR pro cm² Material/Tinte (linear scaling)
  SETUP_PER_STICKER: 0.072,  // EUR Sockelbetrag pro Sticker (Druck-Setup, Stanzen)
  MIN_UNIT_PRICE:    0.05,   // Hartfloor pro Stück (Sicherheitsnetz, greift fast nie)
  MIN_ORDER_TOTAL:   50,     // Absolute Untergrenze Gesamtpreis (net EUR)
  TIER_STEP_UP:      10,     // Mind. €X teurer als der vorherige Tier (greift nur,
                             //   wenn der vorherige Tier selbst noch im Floor lag)

  // Shape complexity multiplier — eckige Stanzformen teurer (mehr Materialverlust,
  // komplexere Setups) als runde. Mit Hybrid-Modell ergibt das saubere Differenzierung
  // zwischen Quadrat/Rechteck/Rund auch bei gleicher cm²-Fläche.
  SHAPE_MULTIPLIER: {
    square:    1.13,
    rectangle: 1.15,
    round:     1.00
  },

  // Discounts kalibriert gegen Konkurrenz-Mengenstaffel:
  //  Konkurrenz 5×5: 1000=€100, 2000=€170 (-15%/Stk), 5000=€350 (-30%/Stk)
  QUANTITY_TIERS: [
    { qty: 100,  discount: 0.00 },
    { qty: 250,  discount: 0.18 },
    { qty: 500,  discount: 0.30 },
    { qty: 1000, discount: 0.41 },
    { qty: 2000, discount: 0.50 },
    { qty: 5000, discount: 0.58 }
  ],

  // Round stickers use the full square around them as material, so large round
  // stickers in big quantities get a smaller discount (>= 20 % margin, Sep 2026).
  SHAPE_DISCOUNT_CAP: { round: 0.44 },

  SHAPES: {
    square: {
      label: 'Quadrat',
      icon: 'square',
      sizes: [
        { id: 'sq-5',  w: 5,  h: 5,  label: '5 × 5 cm' },
        { id: 'sq-7',  w: 7,  h: 7,  label: '7 × 7 cm' },
        { id: 'sq-10', w: 10, h: 10, label: '10 × 10 cm' },
        { id: 'sq-12', w: 12, h: 12, label: '12 × 12 cm' }
      ]
    },
    rectangle: {
      label: 'Rechteck',
      icon: 'rectangle',
      sizes: [
        { id: 'rc-5x7',  w: 5,  h: 7,  label: '5 × 7 cm' },
        { id: 'rc-7x10', w: 7,  h: 10, label: '7 × 10 cm' },
        { id: 'rc-6x12', w: 6,  h: 12, label: '6 × 12 cm' },
        { id: 'rc-4x20', w: 4,  h: 20, label: '4 × 20 cm' }
      ]
    },
    round: {
      label: 'Rund',
      icon: 'circle',
      sizes: [
        { id: 'rd-5',  w: 5,  h: 5,  label: 'Ø 5 cm' },
        { id: 'rd-7',  w: 7,  h: 7,  label: 'Ø 7 cm' },
        { id: 'rd-10', w: 10, h: 10, label: 'Ø 10 cm' },
        { id: 'rd-12', w: 12, h: 12, label: 'Ø 12 cm' }
      ]
    }
  },

  CUSTOM_MIN_CM: 2,
  CUSTOM_MAX_CM: 95,

  // Shopify variants: SKU = PREFIX-SHAPE-SIZE-QTY, e.g. EGG-SQ-7-100, EGG-RC-7x10-250,
  // EGG-RD-5-1000 (round: diameter). Standard sizes are variants of the main
  // product. Custom sizes of square and round are variants of the hidden size
  // products, priced in steps (rounded up): 0.5 cm up to 15.5 cm, 1 cm up to 30 cm.
  // Everything else (custom rectangles, sizes over 30 cm) goes to the hidden
  // helper product at 0.01 EUR (quantity = price in cents).
  // After changing prices here, update the variant prices in Shopify:
  //   node shopify-tools/de_variants.js sizes|main --go
  SKU_PREFIX: 'EGG',
  SHAPE_CODE: { square: 'SQ', rectangle: 'RC', round: 'RD' },
  POPULAR_QTY: 1000,
  CUSTOM_SNAP: [ { max: 15.5, step: 0.5 }, { max: 30, step: 1 } ],
  WEIGHT: { gramsPerM2: 220, marginCm: 0.4, packagingG: 120 },
  LOCALE: 'de-DE'
};

// -----------------------------------------------------------------------------
// AREA CALCULATION
// -----------------------------------------------------------------------------
function calcArea(shape, w, h) {
  if (shape === 'round') {
    const r = w / 2;
    return Math.PI * r * r;
  }
  return w * h;
}

// -----------------------------------------------------------------------------
// CORE: PRICE FOR A GIVEN AREA + QTY (+ optional shape for complexity multiplier)
// -----------------------------------------------------------------------------
function priceFor(areaCm2, qty, shape) {
  const tier = PRICING.QUANTITY_TIERS.find(t => t.qty === qty)
    || closestTier(qty);

  const shapeMult = (shape && PRICING.SHAPE_MULTIPLIER[shape]) || 1.0;

  // Hybrid: Setup-Sockel + Material pro cm², dann shape-Multiplikator
  const linearPerUnit = (PRICING.SETUP_PER_STICKER + PRICING.BASE_PER_CM2 * areaCm2) * shapeMult;

  const basePerUnit = Math.max(
    PRICING.MIN_UNIT_PRICE,
    linearPerUnit
  );

  // NO floor on the discounted price — discounts work freely below MIN_UNIT_PRICE.
  const discount = discountFor(shape, tier.qty);
  const discountedPerUnit = basePerUnit * (1 - discount);

  const rawTotal = discountedPerUnit * qty;

  // Hard floor 1: minimum order value
  const minimumApplied = rawTotal < PRICING.MIN_ORDER_TOTAL;
  let total = minimumApplied ? PRICING.MIN_ORDER_TOTAL : rawTotal;
  let tieredStepApplied = false;

  // Hard floor 2: each tier must be at least TIER_STEP_UP more expensive than the
  // previous tier — but ONLY if the previous tier was itself constrained (by MIN_ORDER
  // or by an earlier tier-step). Once organic pricing kicks in, no further enforcement.
  const tierIdx = PRICING.QUANTITY_TIERS.findIndex(t => t.qty === qty);
  if (tierIdx > 0 && PRICING.TIER_STEP_UP > 0) {
    const prevTier = PRICING.QUANTITY_TIERS[tierIdx - 1];
    const prev = priceFor(areaCm2, prevTier.qty, shape);
    if (prev.minimumApplied || prev.tieredStepApplied) {
      const stepFloor = prev.total + PRICING.TIER_STEP_UP;
      if (total < stepFloor) {
        total = stepFloor;
        tieredStepApplied = true;
      }
    }
  }

  // Always round UP to a full euro (Sep 2026): this is exactly the price of the
  // Shopify variant. (Cents first, so float noise like 50.0000001 stays 50.)
  total = Math.ceil(Math.round(total * 100) / 100 - 1e-9);

  // Effective per-unit reflects the minimum when it kicks in
  const effectivePerUnit = total / qty;

  return {
    qty,
    areaCm2,
    basePerUnit,
    discountedPerUnit: effectivePerUnit,        // shown to customer
    discount,
    total,
    minimumApplied,                             // true → MIN_ORDER hard floor active
    tieredStepApplied,                          // true → TIER_STEP_UP rule pushed price up
    floorActive: minimumApplied || tieredStepApplied  // convenience for UI
  };
}

// Quantity discount of a tier, capped per shape (see SHAPE_DISCOUNT_CAP)
function discountFor(shape, qty) {
  const tier = PRICING.QUANTITY_TIERS.find(t => t.qty === qty) || closestTier(qty);
  const cap = PRICING.SHAPE_DISCOUNT_CAP && shape != null ? PRICING.SHAPE_DISCOUNT_CAP[shape] : undefined;
  return cap != null ? Math.min(tier.discount, cap) : tier.discount;
}

function closestTier(qty) {
  return PRICING.QUANTITY_TIERS.reduce((a, b) =>
    Math.abs(b.qty - qty) < Math.abs(a.qty - qty) ? b : a
  );
}

// -----------------------------------------------------------------------------
// SAVINGS vs. SMALLEST TIER ("Du sparst × EUR")
// -----------------------------------------------------------------------------
function calcSavings(areaCm2, qty, shape) {
  const ref = priceFor(areaCm2, PRICING.QUANTITY_TIERS[0].qty, shape); // 100 Stk baseline
  const cur = priceFor(areaCm2, qty, shape);

  const referencePerUnit = ref.discountedPerUnit;
  const currentPerUnit   = cur.discountedPerUnit;

  const wouldHaveCost  = referencePerUnit * qty;
  const savedAbsolute  = Math.max(0, wouldHaveCost - cur.total);
  const savedPercent   = referencePerUnit > 0 && currentPerUnit < referencePerUnit
    ? ((referencePerUnit - currentPerUnit) / referencePerUnit) * 100
    : 0;

  return {
    referencePerUnit,
    currentPerUnit,
    savedAbsolute,
    savedPercent
  };
}

// -----------------------------------------------------------------------------
// FORMATTERS  (named fmtEUR / fmtEURcents — used by configurator.js)
// -----------------------------------------------------------------------------
// Totals: full euros without ",00" (63 €); other amounts with cents (12,40 €)
function fmtEUR(n) {
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  return new Intl.NumberFormat(PRICING.LOCALE || 'de-DE', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2
  }).format(whole ? Math.round(n) : n);
}

function fmtEURcents(n) {
  return new Intl.NumberFormat(PRICING.LOCALE || 'de-DE', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(n);
}

// -----------------------------------------------------------------------------
// SHOPIFY VARIANTS: SKU, custom size steps, shipping weight
// -----------------------------------------------------------------------------
function fmtNum(n) { return String(Math.round(n * 10) / 10); }
function skuFor(shape, w, h, qty) {
  const size = shape === 'rectangle' ? fmtNum(w) + 'x' + fmtNum(h) : fmtNum(w);
  return PRICING.SKU_PREFIX + '-' + PRICING.SHAPE_CODE[shape] + '-' + size + '-' + qty;
}
// Next price step for a custom size (null = no variant, use the helper product)
function snapSize(n) {
  for (const b of PRICING.CUSTOM_SNAP) {
    const s = Math.round(Math.ceil(n / b.step - 1e-9) * b.step * 10) / 10;
    if (s <= b.max + 1e-9) return s;
  }
  return null;
}
function estimateWeightGrams(shape, w, h, qty) {
  const m = PRICING.WEIGHT;
  const m2 = (w + m.marginCm) * (h + m.marginCm) / 10000;
  return Math.round(m2 * qty * m.gramsPerM2 * 1.1 + m.packagingG);
}

if (typeof module !== 'undefined') {
  module.exports = { PRICING, calcArea, priceFor, discountFor, calcSavings, fmtEUR, fmtEURcents, skuFor, snapSize, estimateWeightGrams };
}
